import { useInviteNewUserMutation } from '@/api/api.slice.tsx';
import { useAppDispatch, useAppSelector } from '@/app/hooks.ts';
import {
  getAccountsState,
  setDialogInviteOpen,
  setInputErrors,
  setInviteCompanyName,
  setInviteContactName,
  setInviteEmail,
} from '@/features/accounts/accounts.slice.ts';
import {
  Alert,
  Button,
  Dialog,
  TextField,
  Typography,
} from '@arsnovasystems/ars-front-lib';
import { Stack } from '@mui/material';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

const regexEmail = new RegExp(
  // eslint-disable-next-line i18next/no-literal-string
  '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$',
);

/**
 * Invite new user Dialog
 *
 * @returns JSX of the component
 */
export const InviteNewUserDialog = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    dialogInviteOpen: dialogInviteUserOpen,
    inviteEmail,
    inviteCompanyName,
    inviteContactName,
    inputErrors,
  } = useAppSelector(getAccountsState);

  const [inviteUser, { isLoading: inviteUserIsLoading }] =
    useInviteNewUserMutation();

  const inviteUserHandler = async () => {
    const isValid = validateEmail();

    if (!isValid) return;

    const { error } = await inviteUser({
      email: inviteEmail,
      companyName: inviteCompanyName,
      contactName: inviteContactName,
    });

    if (!error) {
      dispatch(setDialogInviteOpen(false));
    }
  };

  /**
   * Function to edit partner's email
   *
   * @returns either the email is valid or not
   */
  const validateEmail = (): boolean => {
    if (inviteEmail === '') {
      dispatch(
        setInputErrors({
          input: 'inviteEmail',
          error: t('Email field is required'),
        }),
      );
      return false;
    }
    if (!regexEmail.test(inviteEmail ?? '')) {
      dispatch(
        setInputErrors({
          input: 'inviteEmail',
          error: t('The format seems incorrect'),
        }),
      );
      return false;
    }

    return true;
  };

  return (
    <Dialog
      open={dialogInviteUserOpen}
      title={t('ik.invite.a.new.user')}
      actions={[
        <Button
          onClick={() => {
            dispatch(setDialogInviteOpen(false));
          }}
        >
          {t('ik.cancel')}
        </Button>,
        <Button
          loading={inviteUserIsLoading}
          type='submit'
          onClick={inviteUserHandler}
        >
          {t('ik.send')}
        </Button>,
      ]}
      onClose={() => {
        dispatch(setDialogInviteOpen(false));
      }}
    >
      <Fragment>
        <Typography sx={{ mt: 4 }}>
          {t('ik.customerAccounts.inviteNewUser.description')}
        </Typography>
        <Stack>
          <TextField
            sx={{
              marginTop: 3,
            }}
            fullWidth
            label={t('ik.accounts.customerEmail')}
            value={inviteEmail}
            helperText={inputErrors.inviteEmail}
            error={!!inputErrors.inviteEmail}
            onChange={(e) => {
              dispatch(setInviteEmail(e.target.value));
            }}
            required
          />
          <Alert
            severity={'info'}
            sx={{
              marginTop: 2,
            }}
          >
            {t('ik.customerAccounts.inviteNewUser.privateField.description')}
          </Alert>
          <TextField
            sx={{
              marginTop: 4,
            }}
            fullWidth
            label={t('ik.contact.name')}
            value={inviteContactName}
            onChange={(e) => {
              dispatch(setInviteContactName(e.target.value));
            }}
          />
          <TextField
            sx={{
              marginTop: 2,
            }}
            fullWidth
            label={t('ik.accounts.companyName')}
            value={inviteCompanyName}
            onChange={(e) => {
              dispatch(setInviteCompanyName(e.target.value));
            }}
          />
        </Stack>
      </Fragment>
    </Dialog>
  );
};
